<script setup>
import { format } from 'date-fns';
import { computed, onMounted } from 'vue';
import CrmAPI from '@/dashboard/api/integrations/crm';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  deal: {
    type: Object,
    required: true,
  },
  integrationId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['stageUpdated']);

const CrmApiIntegration = new CrmAPI(props.integrationId);

const formattedDate = computed(() => {
  const { created_at } = props.deal;
  return format(new Date(created_at), 'HH:mm, MMM dd');
});

const assignee = computed(() => {
  const user = props.deal.user;

  if (!user) return null;
  const { name, nickname } = user;

  return {
    name,
    nickname,
  };
});

const contact = computed(() => {
  return props.deal.contacts?.[0] || null;
});

const customFields = computed(() => {
  return props.deal.deal_custom_fields || [];
});

const totalAmount = computed(() => {
  return props.deal.amount_total || 0;
});

const products = computed(() => {
  return props.deal.deal_products || [];
});

const ratingStars = computed(() => {
  const rating = props.deal.rating || 0;
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
});

const pipelineStages = computed(() => {
  return Object.values(props.deal.pipeline_stages) || [];
});

const wonStage = computed(() => {
  return pipelineStages.value.find(stage => stage.code === 'won') || null;
});

const lostStage = computed(() => {
  return pipelineStages.value.find(stage => stage.code === 'lost') || null;
});

const status = computed(() => {
  const dealStageId = props.deal.deal_stage?.id;
  const pipelineStages = Object.values(props.deal.pipeline_stages);
  if (!dealStageId) return null;

  const matchedStage = pipelineStages.find(stage => stage.id === dealStageId);

  return matchedStage?.code || null;
});

const updateStage = async stageId => {
  if (!stageId) return;

  try {
    const accountId = window.location.pathname.split('/')[3];
    const response = await CrmApiIntegration.updateDealStage({
      deal_id: props.deal.id,
      stage_id: stageId,
    });
    
    if (response.data) {
      console.log('Deal stage updated successfully');
      useAlert('Estágio da negociação atualizado com sucesso');
      emit('stageUpdated');
    }
  } catch (error) {
    console.error('Error updating deal stage:', error);
    useAlert('Erro ao atualizar o estágio da negociação');
  }
};

onMounted(() => {
  // Component mounted
});
</script>

issue
<template>
  <div
    class="flex flex-col items-start bg-white dark:bg-slate-800 z-50 px-4 py-3 border border-solid border-ash-200 rounded-xl gap-4 max-h-96 overflow-auto"
  >
    <!-- Deal Title and Description -->
    <div class="flex flex-col w-full">
      <span class="mt-2 text-sm font-medium text-ash-900">
        {{ deal.name }}
      </span>
      <span class="mt-1 text-xs text-ash-600"> ID: {{ deal.id }} </span>
    </div>

    <!-- Contact Information -->
    <div v-if="contact" class="flex flex-col w-full">
      <span class="text-xs font-medium text-ash-900 mb-1">Contato:</span>
      <span class="text-sm text-ash-800">{{ contact.name }}</span>
      <span v-if="contact.phones?.length" class="text-xs text-ash-600">
        {{ contact.phones[0].phone }}
      </span>
    </div>

    <!-- Deal Stage and User -->
    <div class="flex flex-row items-center w-full gap-4">
      <div v-if="props.integrationId === 'welloncrm'" class="flex items-center gap-2">
        <fluent-icon icon="status" size="14" class="text-blue-600" />
        <span class="text-xs text-ash-900">
          {{ deal.deal_pipeline?.name }}
        </span>
      </div>

      <div class="flex items-center gap-2">
        <fluent-icon icon="status" size="14" class="text-blue-600" />
        <span class="text-xs text-ash-900">
          {{ deal.deal_stage?.name }}
        </span>
      </div>

      <div v-if="assignee" class="flex items-center gap-2">
        <fluent-icon icon="person" size="14" class="text-green-600" />
        <span class="text-xs text-ash-900">
          {{ assignee.name }}
        </span>
      </div>
    </div>

    <!-- Rating and Amount -->
    <div v-if="props.integrationId !== 'welloncrm'" class="flex flex-row items-center w-full gap-4">
      <div class="flex items-center gap-2">
        <span class="text-xs text-ash-600">Avaliação:</span>
        <span class="text-sm text-yellow-600">{{ ratingStars }}</span>
      </div>

      <div class="flex items-center gap-2">
        <span class="text-xs text-ash-600">Valor:</span>
        <span class="text-sm font-medium text-green-600">
          R$ {{ totalAmount }}
        </span>
      </div>
    </div>

    <!-- Products -->
    <div v-if="products.length" class="flex flex-col w-full">
      <span class="text-xs font-medium text-ash-900 mb-2">Produtos:</span>
      <div class="flex flex-wrap gap-1">
        <span
          v-for="product in products"
          :key="product.id"
          class="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
        >
          {{ product.name.trim() }} (R$ {{ product.total }})
        </span>
      </div>
    </div>

    <!-- Important Custom Fields -->
    <div v-if="customFields.length" class="flex flex-col w-full">
      <span class="text-xs font-medium text-ash-900 mb-2">Detalhes:</span>
      <div class="grid grid-cols-1 gap-2">
        <div
          v-for="field in customFields.slice(0, 3)"
          :key="field.custom_field_id"
          class="flex flex-col gap-1"
        >
          <span class="text-xs text-ash-600">
            {{ field.custom_field.label }}:
          </span>
          <!-- Multiple choice field - display as tags -->
          <div
            v-if="
              field.custom_field.type === 'multiple_choice' &&
              Array.isArray(field.value)
            "
            class="flex flex-wrap gap-1"
          >
            <span
              v-for="value in field.value"
              :key="value"
              class="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full"
            >
              {{ value }}
            </span>
          </div>
          <!-- Single value field -->
          <span
            v-else
            class="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full w-fit"
          >
            {{
              Array.isArray(field.value) ? field.value.join(', ') : field.value
            }}
          </span>
        </div>
      </div>
    </div>

    <div v-if="props.integrationId === 'welloncrm'" class="flex flex-row items-center w-full justify-center gap-4">
      <div class="flex items-center gap-2">
        <button
          class="flex items-center gap-1 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
          :disabled="status === 'lost'"
          :style="{ cursor: status === 'lost' ? 'not-allowed' : 'pointer' }"
          @click="updateStage(lostStage?.id)"
        >
          <fluent-icon icon="dismiss" size="14" />
          <span>{{ lostStage.name }}</span>
        </button>
        <button
          class="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
          :disabled="status === 'won'"
          :style="{ cursor: status === 'won' ? 'not-allowed' : 'pointer' }"
          @click="updateStage(wonStage?.id)"
        >
          <fluent-icon icon="checkmark" size="14" />
          <span>{{ wonStage.name }}</span>
        </button>
      </div>
    </div>

    <!-- Created Date -->
    <div class="flex items-center w-full pt-2 border-t border-ash-200">
      <span class="text-xs text-ash-600"> Criado em: {{ formattedDate }} </span>
    </div>
  </div>
</template>
